import { Controller } from "@hotwired/stimulus"

// One form shown a step at a time.
//
// The fields are all in the DOM throughout — hidden, not unmounted — so the
// form submits as one POST and nothing has to be carried between steps.
// That also means a browser with no JavaScript gets every step at once,
// which is a long form rather than a broken one.
//
// WHY NOT SEPARATE REQUESTS PER STEP. A half-finished food would then need
// somewhere to live between them: either a draft row in the table, or the
// session. Both are state to invalidate, and both can strand a row nobody
// finishes. One POST at the end has neither problem.
export default class extends Controller {
  static targets = ["step", "back", "next", "submit", "progress", "exit", "subtitle"]

  connect() {
    this.index = 0
    this.show()
  }

  next() {
    // The browser's own validation, not a reimplementation of it: `required`
    // on the name field is what makes step 1 gate, and checkValidity() also
    // draws the message. Without this you can page past an empty name and
    // only find out on submit, three screens later.
    if (!this.valid(this.currentStep)) return

    if (this.index >= this.stepTargets.length - 1) return

    this.index++
    this.show()
  }

  back() {
    if (this.index === 0) return

    this.index--
    this.show()
  }

  // THE HEADER'S BACK, which is a different control from the footer's.
  //
  // It used to leave the modal from any step, skipping the wizard — so from
  // step 3 you lost two screens of typing in one tap with no warning. It
  // now walks back through the steps and only leaves from the first, where
  // there is genuinely nothing behind it but the picker.
  exit(event) {
    if (this.index === 0) return

    event.preventDefault()
    this.back()
  }

  get currentStep() {
    return this.stepTargets[this.index]
  }

  // reportValidity draws the browser's own bubble on the offending field,
  // which is the whole point — a silent refusal to advance reads as a
  // broken button.
  valid(step) {
    const fields = step.querySelectorAll("input, select, textarea")

    return Array.from(fields).every((field) => field.reportValidity())
  }

  show() {
    this.stepTargets.forEach((step, i) => {
      step.hidden = i !== this.index
    })

    const last = this.index === this.stepTargets.length - 1

    // Back is absent on the first step rather than disabled: a dead control
    // is something to try, and there is nothing behind step 1.
    if (this.hasBackTarget) this.backTarget.hidden = this.index === 0

    // The header's Back says where it goes. On steps 2 and 3 that is the
    // previous step, not the picker.
    if (this.hasExitTarget) {
      const first = this.index === 0
      this.exitTarget.setAttribute(
        "aria-label", first ? this.exitTarget.dataset.exitLabel : "Back a step"
      )
    }
    // A PLAIN "Next". The design named the screen ahead — "Next — the
    // numbers" — which reads well on one drawn screen and badly on a real
    // button that has to fit at 375px beside a Back. Cody's call, 4 Sep.
    if (this.hasNextTarget) this.nextTarget.hidden = last
    if (this.hasSubmitTarget) this.submitTarget.hidden = !last

    // THE SUBTITLE FOLLOWS THE STEP — "What's in it", "How you measure it".
    // The design puts the step's name in the modal's subtitle rather than
    // repeating it as a heading inside the body, which is how the same words
    // ended up on screen twice.
    //
    // Step 1 has none, so it falls back to whatever the modal was rendered
    // with — "Back to foods", which is what says where the header's Back
    // goes.
    if (this.hasSubtitleTarget) {
      const step = this.currentStep.dataset.stepSubtitle
      this.subtitleTarget.textContent = step || this.subtitleTarget.dataset.default || ""
      this.subtitleTarget.hidden = !this.subtitleTarget.textContent
    }

    if (this.hasProgressTarget) {
      this.progressTarget.textContent = `Step ${this.index + 1} of ${this.stepTargets.length}`
    }

    // Focus the first field of the step that just appeared, so a keyboard
    // lands in the form rather than on the button that brought it here.
    this.currentStep.querySelector("input, select, textarea")?.focus()
  }

  // Called when the form is reset and reused — the dialog does this after a
  // successful save, and without it the next open starts on step 3.
  restart() {
    this.index = 0
    this.show()
  }
}
